/*eslint max-lines-per-function: ["error", 260]*/
import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateCardDto,
  CreateUpdateCardDtoTypeEnum,
  CreateUpdateCardDtoPdfTemplateTypeEnum,
  GetExamDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  CardType,
  TemplateType,
} from '../../../common/spdCore/validation/schemas';
import { validate } from '../../../common/spdCore/contexts/CardContext';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { AlertHarmfulFactorsAndCriticalPoints } from '../../containers/ExamTestsView/AlertHarmfulFactorsAndCriticalPoints';

import { CardSportEditor } from './CardSportEditor';
import { SupportingExaminationStatusAlert } from './SupportingExaminationStatusAlert';
import { CardSanitaryEditor } from './CardSanitaryEditor';
import { CardDriverEditor } from './CardDriverEditor';
import { CardPreventiveEditor } from './CardPreventiveEditor';
import { CardWeaponEditor } from './CardWeaponEditor';
import { CardPsychologicalEditor } from './CardPsychologicalEditor';

interface CardEditorProps {
  exam: GetExamDto;
  card: CreateUpdateCardDto;
  type: TemplateType;
  id: string | undefined;
}

export const CardEditor: React.FC<CardEditorProps> = props => {
  const spd = useSpdCore();
  const [card, setCard] = useState<CreateUpdateCardDto>(props.card);
  const [id, setId] = useState<string | undefined>(props.id);
  const [message, setMessage] = useState<string>('');
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [cardType, setCardType] = useState<CardType>();
  const [title, setTitle] = useState<string>('');
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [supportRefreshToggle, setSupportRefreshToggle] = useState(false);

  useEffect(() => {
    setCardType(
      props.type === TemplateType.Psychological_Exam_card
        ? CardType.Psychologist
        : props.type === TemplateType.Weapon_psychological_exam_card
          ? CardType.PsychologistWeapon
          : props.type === TemplateType.Weapon_card
            ? CardType.Weapon
            : props.type === TemplateType.Preventive_Exam_card
              ? CardType.Preventive
              : props.type === TemplateType.Driver_Exam_card
                ? CardType.Driver
                : props.type === TemplateType.Sanitary_Exam_card
                  ? CardType.Sanitary
                  : props.type === TemplateType.Sport_card
                    ? CardType.Sport
                    : CardType.Driver,
    );
    setTitle(
      props.type === TemplateType.Psychological_Exam_card
        ? 'Karta badania psychologicznego'
        : props.type === TemplateType.Weapon_psychological_exam_card
          ? 'Karta badania psychologicznego na broń'
          : props.type === TemplateType.Weapon_card
            ? 'Karta badania na broń'
            : props.type === TemplateType.Preventive_Exam_card
              ? 'Karta badania profilaktycznego'
              : props.type === TemplateType.Driver_Exam_card
                ? 'Karta badania kierowcy'
                : props.type === TemplateType.Sanitary_Exam_card
                  ? 'Karta badania sanitarno epidemiologiczna'
                  : props.type === TemplateType.Sport_card
                    ? 'Karta badania sportowca'
                    : 'Karta badania kierowcy',
    );
  }, [props.type]);

  const createCard = async (gCard: CreateUpdateCardDto) => {
    setMessage('');
    const status = await validate(gCard);
    if (!status.valid) {
      setSaveErrors(status.errors);
      setShowSaveErrors(true);
      return;
    }
    setShowSaveErrors(false);
    try {
      if (id) {
        setLoadingSave(true);
        await spd.cards.update(id, gCard);
        setCard(gCard);

        await spd.pdfPrinter.saveAndPrintCard(id);
        setLoadingSave(false);
        setSupportRefreshToggle(v => !v);
      } else {
        setLoadingSave(true);
        await spd.cards
          .create({
            ...gCard,
            ...{ examId: props?.exam?.id },
          })
          .then(async o => {
            setCard({
              ...o,
              ...{
                ...gCard,
                ...{
                  pdfTemplateType:
                    o.pdfTemplateType as unknown as CreateUpdateCardDtoPdfTemplateTypeEnum,

                  examId: props?.exam?.id,
                  type: cardType as unknown as CreateUpdateCardDtoTypeEnum,
                  number: o.number,
                },
              },
            });
            setId(o.id);
            await spd.pdfPrinter.saveAndPrintCard(o.id);
            setLoadingSave(false);
            setSupportRefreshToggle(v => !v);
          })
          .catch(err => {
            setLoadingSave(false);
            void responseErrors(err).then(errors => {
              setMessage(`Błąd podczas zapisywania karty badania:\n ${errors}`);
            });
          });
      }
    } catch (response) {
      setLoadingSave(false);
      const error = await responseErrors(response as ResponseError);
      setMessage(`Błąd podczas zapisywania karty badania:\n ${error}`);
    }
  };

  const readCard = async () => {
    setMessage('');
    try {
      if (id) {
        await spd.pdfPrinter.loadAndPrintCard(id);
      }
    } catch (response) {
      const error = await responseErrors(response as ResponseError);
      setMessage(`Błąd podczas podglądu wydruku karty badania:\n ${error}`);
    }
  };

  const sendEmail = async (id: string) => {
    spd.emails
      .sendPsychologicalCardSummary(id)
      .then(() => setMessage(`Wysłano wiadomość e-mail`))
      .catch(() =>
        setMessage(
          `Wystąpił błąd podczas wysyłania podsumowania badania psychologicznego do badanego.`,
        ),
      );
  };

  const propertyChange = (obj: Partial<CreateUpdateCardDto>) => {
    if (obj.examFrequencyChange === false) {
      obj.examFrequencyChangeJustification = null;
    }
    setCard({ ...card, ...obj });
  };

  return (
    <Card className="mb-2">
      <Card.Header>{`${title} ${props.exam?.number}`}</Card.Header>
      <Card.Body>
        <AlertHarmfulFactorsAndCriticalPoints exam={props.exam} />
        {props.exam?.id && (
          <SupportingExaminationStatusAlert
            key={String(supportRefreshToggle)}
            examId={props.exam.id}
            exam={props.exam}
          />
        )}
        <Form className="d-grid gap-3">
          {props.type === TemplateType.Psychological_Exam_card && (
            <CardPsychologicalEditor
              card={card}
              id={id}
              propertyChange={propertyChange}
            />
          )}
          {props.type === TemplateType.Weapon_psychological_exam_card && (
            <CardPsychologicalEditor
              card={card}
              id={id}
              propertyChange={propertyChange}
            />
          )}
          {props.type === TemplateType.Weapon_card && (
            <CardWeaponEditor
              exam={props.exam}
              card={card}
              id={id}
              propertyChange={propertyChange}
            />
          )}
          {props.type === TemplateType.Preventive_Exam_card && (
            <CardPreventiveEditor
              exam={props.exam}
              card={card}
              id={id}
              propertyChange={propertyChange}
              onSupportChange={() => setSupportRefreshToggle(v => !v)}
            />
          )}
          {props.type === TemplateType.Driver_Exam_card && (
            <CardDriverEditor
              exam={props.exam}
              card={card}
              id={id}
              propertyChange={propertyChange}
              onSupportChange={() => setSupportRefreshToggle(v => !v)}
            />
          )}
          {props.type === TemplateType.Sanitary_Exam_card && (
            <CardSanitaryEditor
              exam={props.exam}
              card={card}
              id={id}
              propertyChange={propertyChange}
              onSupportChange={() => setSupportRefreshToggle(v => !v)}
            />
          )}
          {props.type === TemplateType.Sport_card && (
            <CardSportEditor
              card={card}
              id={id}
              propertyChange={propertyChange}
            />
          )}
          {loadingSave && (
            <Alert variant="warning">
              <Spinner size="sm" className="mx-2" />
              Trwa generowanie karty. Proszę nie zamykać tej strony.
            </Alert>
          )}
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
          {message && <Alert variant="danger">{message}</Alert>}
          <Button
            variant="outline-primary"
            className="ms-1"
            onClick={() => createCard(card)}
          >
            Zapisz i wydrukuj
          </Button>
          {id && (
            <Button
              variant="outline-secondary"
              className="ms-1"
              onClick={() => readCard()}
            >
              Pokaż wydruk
            </Button>
          )}
          {id &&
            (props.type === TemplateType.Psychological_Exam_card ||
              props.type === TemplateType.Weapon_psychological_exam_card) && (
              <ConfirmationButton
                className="ms-1"
                confirmation="Czy na pewno przesłać informację o wyniku przeprowadzonego badania psychologicznego do badanego?"
                variant="outline-secondary"
                onOK={() => sendEmail(id)}
              >
                Wyślij wynik do badanego
              </ConfirmationButton>
            )}
        </Form>
      </Card.Body>
    </Card>
  );
};
